import React, { useState } from "react";
import { api } from "../lib/api";

const Updates = () => {
  // Existing updates-list form
  const [form, setForm] = useState({
    name: "",
    email: "",
    trader_type: "",
  });

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Feedback form
  const [selectedTools, setSelectedTools] = useState([]);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  const feedbackTools = [
    "Risk Calculator / Risk Engine",
    "Trade Journal",
    "Overview / Analytics",
    "AI Insights",
    "Strategy Lab",
    "Advanced Analytics",
    "Brokerage Integrations",
    "Other",
  ];

  // -------------------------
  // Updates List
  // -------------------------

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess(false);

    try {
      await api("/updates", {
        method: "POST",
        public: true,
        body: JSON.stringify(form),
      });

      setSuccess(true);

      setForm({
        name: "",
        email: "",
        trader_type: "",
      });
    } catch (err) {
      setError(err?.message || "Unable to join updates list.");
    }
  };

  // -------------------------
  // Feedback
  // -------------------------

  const toggleTool = (tool) => {
    setSelectedTools((current) =>
      current.includes(tool)
        ? current.filter((item) => item !== tool)
        : [...current, tool]
    );
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();

    setFeedbackError("");
    setFeedbackSuccess(false);

    if (!feedbackText.trim()) {
      setFeedbackError(
        "Tell us what you'd like to see before submitting."
      );
      return;
    }

    if (feedbackText.trim().length > 10000) {
      setFeedbackError(
        "Feedback must be 10,000 characters or fewer."
      );
      return;
    }

    setFeedbackSubmitting(true);

    try {
      await api("/feedback", {
        method: "POST",
        public: true,
        body: JSON.stringify({
          tools: selectedTools,
          feedback_text: feedbackText.trim(),
        }),
      });

      setFeedbackSuccess(true);
      setSelectedTools([]);
      setFeedbackText("");
    } catch (err) {
      setFeedbackError(
        err?.message || "Unable to submit feedback."
      );
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent px-4 py-12 text-white">
      <div className="mx-auto w-full max-w-4xl">

        {/* Page Header */}
        <div className="mb-7 rounded-2xl border border-[#2a2d33] bg-[#15171b] px-8 py-10 text-center shadow-sm sm:px-12 sm:py-12">
          <p className="mb-4 text-xs font-normal uppercase tracking-[0.22em] text-blue-400 sm:text-sm">
            FlowQuant Updates
          </p>

          <h1 className="text-2xl font-semibold tracking-tight text-gray-100 sm:text-[28px]">
            The Future of FlowQuant Is Already in Motion.
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-sm font-normal leading-7 text-gray-400 sm:text-base">
            Join the FlowQuant Updates List and be the first to know when
            new tools, analytics, and trading intelligence go live.
          </p>
        </div>

        {/* Main Grid */}
        <div className="grid gap-5 lg:grid-cols-2">

          {/* Updates List */}
          <section className="rounded-2xl border border-[#2a2d33] bg-[#15171b] p-5 shadow-sm sm:p-6">
            <div className="mb-5">
              <h2 className="text-lg font-medium tracking-tight text-gray-100">
                Join the Updates List
              </h2>

              <p className="mt-2 text-sm font-normal leading-6 text-gray-400">
                New tools are already in motion. Get notified as
                FlowQuant continues to grow.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Name"
                className="w-full rounded-lg border border-[#303645] bg-[#0b0d12] p-3 text-white outline-none transition focus:border-blue-500"
              />

              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Email"
                required
                className="w-full rounded-lg border border-[#303645] bg-[#0b0d12] p-3 text-white outline-none transition focus:border-blue-500"
              />

              <select
                name="trader_type"
                value={form.trader_type}
                onChange={handleChange}
                className="w-full rounded-lg border border-[#303645] bg-[#0b0d12] p-3 text-white outline-none transition focus:border-blue-500"
              >
                <option value="">
                  Select Trader Type
                </option>

                <option>Day Trader</option>
                <option>Swing Trader</option>
                <option>Long Term Investor</option>
                <option>Just Exploring</option>
              </select>

              <button
                type="submit"
                className="w-full rounded-lg bg-blue-600 py-3 font-semibold text-white transition hover:bg-blue-700"
              >
                Join FlowQuant Updates
              </button>
            </form>

            {success && (
              <p className="mt-5 text-sm font-medium text-green-400">
                You're on the list. Welcome to FlowQuant 🚀
              </p>
            )}

            {error && (
              <p className="mt-5 text-sm font-medium text-red-400">
                {error}
              </p>
            )}
          </section>

          {/* Feedback */}
          <section className="rounded-2xl border border-[#2a2d33] bg-[#15171b] p-5 shadow-sm sm:p-6">
            <div className="mb-5">
              <p className="mb-2 text-xs font-medium uppercase tracking-[0.2em] text-blue-500">
                Your Voice Matters
              </p>

              <h2 className="text-lg font-medium tracking-tight text-gray-100">
                Help Shape What We Build Next
              </h2>

              <p className="mt-2 text-sm font-normal leading-6 text-gray-400">
                What would make FlowQuant more useful for the way
                you trade? Select any areas your feedback relates to.
              </p>
            </div>

            <form onSubmit={handleFeedbackSubmit}>

              {/* Feature Multi-Select */}
              <div className="mb-5 flex flex-wrap gap-2">
                {feedbackTools.map((tool) => {
                  const selected = selectedTools.includes(tool);

                  return (
                    <button
                      key={tool}
                      type="button"
                      onClick={() => toggleTool(tool)}
                      aria-pressed={selected}
                      className={`rounded-full border px-3 py-2 text-sm font-normal transition ${
                        selected
                          ? "border-blue-500 bg-blue-600/20 text-blue-300"
                          : "border-[#303645] bg-[#0b0d12] text-gray-300 hover:border-[#465069] hover:text-white"
                      }`}
                    >
                      {tool}
                    </button>
                  );
                })}
              </div>

              {/* Feedback Text */}
              <textarea
                value={feedbackText}
                onChange={(e) =>
                  setFeedbackText(e.target.value)
                }
                rows={5}
                maxLength={10000}
                placeholder="Tell us what you'd like to see, what you'd change, or something you wish FlowQuant could do..."
                className="w-full resize-y rounded-lg border border-[#303645] bg-[#0b0d12] p-3 text-white outline-none transition placeholder:text-gray-600 focus:border-blue-500"
              />

              <p className="mt-2 text-xs text-gray-500">
                Share as much detail as you'd like.
              </p>

              <button
                type="submit"
                disabled={feedbackSubmitting}
                className="mt-5 w-full rounded-lg bg-blue-600 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-[#252a35] disabled:text-gray-500"
              >
                {feedbackSubmitting
                  ? "Submitting..."
                  : "Submit Feedback"}
              </button>

              {feedbackSuccess && (
                <p className="mt-4 text-sm font-medium text-green-400">
                  Got it — thanks for helping shape FlowQuant.
                </p>
              )}

              {feedbackError && (
                <p className="mt-4 text-sm font-medium text-red-400">
                  {feedbackError}
                </p>
              )}
            </form>
          </section>
        </div>

        {/* Roadmap Footer */}
        <div className="mt-6 rounded-2xl border border-[#2a2d33] bg-[#15171b] px-5 py-4 text-center shadow-sm">
          <p className="text-sm font-normal text-gray-300">
            AI Insights. Strategy Lab. Advanced Analytics.
            Brokerage Integrations. And more. FlowQuant is just
            getting started.
          </p>
        </div>

      </div>
    </div>
  );
};

export default Updates;